import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useArenaFit } from './useArenaFit';
import { useCardSwipe } from './useCardSwipe';
import { CoinCard } from './CoinCard';
import { RoundQueue } from './RoundQueue';
import { TokenRail } from './TokenRail';
import { LiveArenaEmpty } from './LiveArenaEmpty';
import { DEFAULT_RAIL_MOTION, type RailMotion } from './railMotion';
import { predictionQueue, railCoins, recentCallOrder } from './arenaLists';
import { ChecklistGlyph, CompassGlyph } from './icons';
import type { ArtRenderer, CoinView } from './model';
import type { Choice, OddsSelection } from '@pons/contracts';
import {
  DEFAULT_QUEUE_MOTION,
  motionEase,
  type QueueMotion,
} from './queueMotion';
export function ArenaScreen({
  coins,
  sidebarCoins = coins,
  emptyState,
  now,
  ready,
  art,
  motion = DEFAULT_QUEUE_MOTION,
  railMotion = DEFAULT_RAIL_MOTION,
  onPredict,
  onExplore,
  onPredictions,
  onGame,
  gameSelected,
}: {
  coins: CoinView[];
  sidebarCoins?: CoinView[];
  emptyState?: ReactNode;
  now: number;
  ready: boolean;
  art: ArtRenderer;
  motion?: QueueMotion;
  railMotion?: RailMotion;
  onPredict: (
    id: string,
    choice: Choice,
    odds?: OddsSelection,
  ) => void | boolean | Promise<boolean>;
  onExplore: () => void;
  onPredictions: () => void;
  onGame?: () => void;
  gameSelected?: boolean;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const lastStart = useRef(0);
  const queue = useMemo(
    () => predictionQueue(coins, now, selectedId),
    [coins, now, selectedId],
  );
  const activeId = queue.some((coin) => coin.id === selectedId)
    ? selectedId
    : ((queue.find((coin) => coin.start > lastStart.current) ?? queue[0])?.id ??
      null);
  const selected = queue.find((coin) => coin.id === activeId);
  const swipe = useCardSwipe(
    activeId,
    queue.map((coin) => coin.id),
    setSelectedId,
  );
  const selectedStart = selected?.start;
  useEffect(() => {
    if (selectedStart !== undefined) lastStart.current = selectedStart;
    if (activeId !== selectedId) setSelectedId(activeId);
  }, [activeId, selectedId, selectedStart]);
  const chosenIds = sidebarCoins
    .filter((coin) => coin.choice)
    .map((coin) => coin.id);
  const [previousCalls, setPreviousCalls] = useState(chosenIds);
  const callOrder = recentCallOrder(previousCalls, chosenIds);
  if (
    callOrder.length !== previousCalls.length ||
    callOrder.some((id, i) => id !== previousCalls[i])
  )
    setPreviousCalls(callOrder);
  const { mine, feed } = railCoins(sidebarCoins, now, callOrder);
  // A newly mounted live rail needs the same resize observer as the existing stage.
  const fit = useArenaFit(Boolean(selected), feed.length > 0);
  return (
    <section
      ref={fit}
      className={
        'arena-screen' +
        (selected ? '' : ' is-waiting') +
        (!selected && !emptyState && onGame ? ' is-live-empty' : '')
      }
    >
      <TokenRail
        side="left"
        label="Your calls"
        coins={mine}
        motion={railMotion}
        queueMotion={motion}
        now={now}
        art={art}
        action={{
          label: 'Your predictions',
          glyph: <ChecklistGlyph />,
          onClick: onPredictions,
        }}
      />
      <div className="arena-stage">
        <div className="arena-card-slot" {...swipe.handlers}>
          {selected ? (
            <div
              key={selected.id}
              className={
                'coin-swap' +
                (motion.cardCascade > 0 ? ' is-cascade' : '') +
                (swipe.arrival ? ' is-swiped' : '')
              }
              style={
                {
                  '--card-ms': motion.cardMs + 'ms',
                  '--card-ease': motionEase(0),
                  '--card-scale': String(motion.cardScale),
                  '--card-blur': motion.cardBlur + 'px',
                  '--card-rise': motion.cardRise + 'px',
                  '--card-shake': motion.cardShake + 'px',
                  '--card-cascade': motion.cardCascade + 'ms',
                  '--swipe-arrival': swipe.arrival * 36 + 'px',
                } as CSSProperties
              }
            >
              <CoinCard
                coin={selected}
                now={now}
                art={art}
                onPredict={onPredict}
              />
            </div>
          ) : emptyState ? (
            <div className="coin-card is-empty">{emptyState}</div>
          ) : onGame ? (
            <LiveArenaEmpty
              ready={ready}
              onGame={onGame}
              gameSelected={gameSelected}
            />
          ) : (
            <div className="coin-card is-empty">
              <h2>
                {ready ? 'Waiting for a graduation.' : 'Loading the arena…'}
              </h2>
              <p>
                New Pons tokens appear here automatically. You get 20 seconds
                from graduation to make your call.
              </p>
            </div>
          )}
        </div>
        <div className="arena-queue-slot">
          <RoundQueue
            coins={queue}
            selectedId={activeId ?? ''}
            now={now}
            art={art}
            motion={motion}
            onSelect={setSelectedId}
          />
        </div>
      </div>
      <TokenRail
        side="right"
        label="Live feed"
        coins={feed}
        motion={railMotion}
        queueMotion={motion}
        now={now}
        art={art}
        action={{
          label: 'Explore rounds',
          glyph: <CompassGlyph />,
          onClick: onExplore,
        }}
      />
    </section>
  );
}
